"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useState } from "react";
import { Check, ChevronLeft, PlusIcon, SaveIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { InputA } from "@/components/atoms/Form/FormInput/InputA";
import { Checkbox } from "@/components/ui/checkbox";
import { useAddFavoritesGroupMutation } from "@/components/modules/favorites/api/mutations/useAddFavoritesGroupMut";
import { useToggleRealEstateInGroup } from "@/components/modules/favorites/api/mutations/useToggleRealEstateInGroup";
import useRealEstateGroups from "@/hooks/QueryHooks/useRealEstateGroups";
import { useQuery } from "@tanstack/react-query";
import { getFavoriteGroupsQueryOptions } from "@/components/modules/favorites/api/query-options";
import RenderImage from "@/components/atoms/RenderImage";
import { cn } from "@/lib/utils";
import { useTranslations } from "next-intl";
type Props = {
  realEstateId: string;
};
const FavoriteMenuContent = t0 => {
  const $ = _c(27);
  const {
    realEstateId
  } = t0;
  const t = useTranslations("modules.favorites.modal");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = getFavoriteGroupsQueryOptions(false);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    data: favoriteGroups,
    isLoading: isLoadingFavoriteGroups
  } = useQuery(t1);
  const {
    data: realEstateFavoriteGroups,
    isLoading
  } = useRealEstateGroups(realEstateId);
  const [isAddingGroup, setIsAddingGroup] = useState(false);
  const [groupName, setGroupName] = useState("");
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => setIsAddingGroup(false);
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    mutate: addGroupMutation
  } = useAddFavoritesGroupMutation(groupName, realEstateId, t2);
  const {
    mutate: toggleRealEstateInGroup
  } = useToggleRealEstateInGroup(realEstateId);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const [checkedGroups, setCheckedGroups] = useState(t3);
  let t4;
  let t5;
  if ($[3] !== realEstateFavoriteGroups) {
    t4 = () => {
      if (realEstateFavoriteGroups) {
        const groupIds = realEstateFavoriteGroups.data.map(_temp);
        setCheckedGroups(groupIds);
      }
    };
    t5 = [realEstateFavoriteGroups];
    $[3] = realEstateFavoriteGroups;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  let t6;
  if ($[6] !== addGroupMutation || $[7] !== groupName || $[8] !== realEstateId) {
    t6 = e => {
      e.stopPropagation();
      e.preventDefault();
      addGroupMutation({
        groupName,
        realEstateId
      });
    };
    $[6] = addGroupMutation;
    $[7] = groupName;
    $[8] = realEstateId;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const handleAddToFavoritesGroup = t6;
  let t7;
  if ($[10] !== checkedGroups || $[11] !== realEstateId || $[12] !== toggleRealEstateInGroup) {
    t7 = groupId => {
      setCheckedGroups(prev => prev.includes(groupId) ? prev.filter(id => id !== groupId) : [...prev, groupId]);
      toggleRealEstateInGroup({
        groupId,
        realEstateId,
        isFavorite: !checkedGroups.includes(groupId)
      });
    };
    $[10] = checkedGroups;
    $[11] = realEstateId;
    $[12] = toggleRealEstateInGroup;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const handleToggleRealEstateInGroup = t7;
  if (isLoadingFavoriteGroups || isLoading) {
    let t8;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <div className="animate-spin h-8 w-8 border-4 border-zinc-800 border-t-transparent rounded-full mx-auto mb-4" />;
      $[14] = t8;
    } else {
      t8 = $[14];
    }
    let t9;
    if ($[15] !== t) {
      t9 = t("loading");
      $[15] = t;
      $[16] = t9;
    } else {
      t9 = $[16];
    }
    let t10;
    if ($[17] !== t9) {
      t10 = <div className="container mx-auto px-4 py-8 flex items-center justify-center md:min-w-[450px] min-h-[450px]"><div className="text-center">{t8}<p className="text-muted-foreground">{t9}</p></div></div>;
      $[17] = t9;
      $[18] = t10;
    } else {
      t10 = $[18];
    }
    return t10;
  }
  if (!favoriteGroups) {
    return;
  }
  let t8;
  if ($[19] !== checkedGroups || $[20] !== favoriteGroups || $[21] !== groupName || $[22] !== handleAddToFavoritesGroup || $[23] !== handleToggleRealEstateInGroup || $[24] !== isAddingGroup || $[25] !== t) {
    t8 = !isAddingGroup ? <div data-testid="favorites-modal-content" className=" md:min-w-[450px] min-h-[450px] flex flex-col justify-between p-4 pr-2 md:p-0" data-sentry-component="FavoriteMenuContent" data-sentry-source-file="FavoriteMenuContent.tsx"><div className="grid grid-cols-2 mb-3 gap-3 max-h-[400px] overflow-auto md:p-0 pr-2 md:pr-2">{favoriteGroups.data.map(group_0 => <div key={group_0.id} data-testid="favorites-modal-group" className="relative flex flex-col items-start w-full cursor-pointer" onClick={() => handleToggleRealEstateInGroup(group_0.id)}><div className={cn("relative w-full aspect-square mb-2 rounded-lg")}>{checkedGroups.includes(group_0.id) && <div className="absolute bottom-2 border-secondary border-2  right-2 bg-white rounded-full h-7 w-7 flex justify-center items-center p-1 z-50 shadow-lg"><Check className="text-green-700" /></div>}<RenderImage alt="" src={group_0.realEstates[0]?.thumbnails?.[0]} className={cn("w-full h-full object-cover rounded-lg")} /></div><label className="text-sm font-semibold capitalize line-clamp-1">{group_0.name}<span className="text-xs text-muted-foreground">{" "}• {group_0.realEstates.length}</span></label></div>)}</div><Button variant="simpleDark" size="lg" data-testid="favorites-add-group-button" onClick={() => setIsAddingGroup(true)} className="w-full mt-2" data-sentry-element="Button" data-sentry-source-file="FavoriteMenuContent.tsx"><PlusIcon className="w-4 h-4" data-testid="favorites-add-group-icon" data-sentry-element="PlusIcon" data-sentry-source-file="FavoriteMenuContent.tsx" />{t("addNewGroup")}</Button></div> : <div data-testid="favorites-create-group-form" className="relative flex flex-col justify-between min-h-[450px] md:min-w-[450px] p-6 md:p-0" data-sentry-component="FavoriteMenuContent" data-sentry-source-file="FavoriteMenuContent.tsx"><div onClick={() => setIsAddingGroup(false)} className="flex items-center gap-2 cursor-pointer text-muted-foreground hover:text-foreground"><ChevronLeft size={20} data-sentry-element="ChevronLeft" data-sentry-source-file="FavoriteMenuContent.tsx" /><span>{t("back")}</span></div><InputA autoComplete="off" value={groupName} maxCharacters={50} onClearButton={() => {
        setGroupName("");
      }} onChange={e_0 => setGroupName(e_0.target.value)} type="text" placeholder={t("groupNamePlaceholder")} data-sentry-element="InputA" data-sentry-source-file="FavoriteMenuContent.tsx" /><Button variant="simpleDark" size="lg" data-testid="favorites-save-group-button" onClick={e_1 => {
        handleAddToFavoritesGroup(e_1);
        setGroupName("");
      }} className="w-full mt-4" data-sentry-element="Button" data-sentry-source-file="FavoriteMenuContent.tsx">{t("saveGroup")}</Button></div>;
    $[19] = checkedGroups;
    $[20] = favoriteGroups;
    $[21] = groupName;
    $[22] = handleAddToFavoritesGroup;
    $[23] = handleToggleRealEstateInGroup;
    $[24] = isAddingGroup;
    $[25] = t;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  return t8;
};
export default FavoriteMenuContent;
function _temp(group) {
  return group.id;
}